import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  clearActivityLogs,
  fetchActivityLogs,
} from "@/store/slices/activityLogSlice";
import { Ionicons } from "@expo/vector-icons";
import { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from "react-native";

export default function ActivitiesScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { logs, pagination, loading } = useAppSelector(
    (state) => state.activityLogs,
  );
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadLogs = useCallback(
    async (page: number, refresh = false) => {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoadingMore(true);
      }
      await dispatch(
        fetchActivityLogs({ page, limit: pagination.itemsPerPage }),
      );
      if (refresh) setRefreshing(false);
      setLoadingMore(false);
    },
    [dispatch, pagination.itemsPerPage],
  );

  useEffect(() => {
    loadLogs(1, true);
    return () => {
      dispatch(clearActivityLogs());
    };
  }, []);

  const handleRefresh = () => {
    loadLogs(1, true);
  };

  const handleLoadMore = () => {
    if (
      !loading &&
      !loadingMore &&
      pagination.currentPage < pagination.totalPages
    ) {
      loadLogs(pagination.currentPage + 1, false);
    }
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getIconForAction = (action: string) => {
    if (action.includes("INVOICE")) return "document-text-outline";
    if (action.includes("CUSTOMER")) return "person-add-outline";
    if (action.includes("EMPLOYEE")) return "people-outline";
    if (action.includes("PRODUCT")) return "cube-outline";
    return "notifications-outline";
  };

  const renderItem = ({ item }: { item: any }) => (
    <View style={[styles.logItem, { borderBottomColor: colors.border }]}>
      <View
        style={[
          styles.iconContainer,
          { backgroundColor: colors.primary + "15" },
        ]}
      >
        <Ionicons
          name={getIconForAction(item.action)}
          size={24}
          color={colors.primary}
        />
      </View>
      <View style={styles.content}>
        <Text style={[styles.description, { color: colors.text }]}>
          {item.description}
        </Text>
        <Text style={[styles.userInfo, { color: colors.textLight }]}>
          {item.user?.ownerName || item.user?.email} •{" "}
          {formatDate(item.createdAt)}
        </Text>
        {item.ipAddress && (
          <Text style={[styles.meta, { color: colors.textLight }]}>
            IP: {item.ipAddress}
          </Text>
        )}
      </View>
    </View>
  );

  const renderFooter = () => {
    if (!loadingMore) return null;
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  };

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <Ionicons name="list-outline" size={64} color={colors.textLight} />
      <Text style={[styles.emptyText, { color: colors.textLight }]}>
        No activity logs found
      </Text>
    </View>
  );

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <FlatList
        data={logs}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderItem}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
          />
        }
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.3}
        ListFooterComponent={renderFooter}
        ListEmptyComponent={!loading && !refreshing ? renderEmpty : null}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  listContent: {
    padding: 16,
  },
  logItem: {
    flexDirection: "row",
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  content: {
    flex: 1,
  },
  description: {
    fontSize: 14,
    fontWeight: "500",
    marginBottom: 4,
  },
  userInfo: {
    fontSize: 12,
    marginBottom: 2,
  },
  meta: {
    fontSize: 11,
  },
  footerLoader: {
    paddingVertical: 16,
    alignItems: "center",
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 64,
  },
  emptyText: {
    fontSize: 16,
    marginTop: 16,
  },
});
